import PropTypes from "prop-types";
import MKTypography from "components/MKTypography";
import Grid from "@mui/material/Grid";
import MKButton from "components/MKButton";
import { Box, IconButton } from "@mui/material";
import Iconify from "components/iconify";
import CartoonCard from "./CartoonCard";

export default function CartoonCardBasicSide({
  title,
  description,
  buttons,
  color = "#ff4e50",
  secondaryColor = "#f9d423",
  tilt = false,
  baseTileAngle = -2,
  endTiltAngle = 3,
  circularButtons = false,
  children,
  image,
}) {
  return (
    <CartoonCard
      color={color}
      secondaryColor={secondaryColor}
      tilt={tilt}
      baseTileAngle={baseTileAngle}
      endTiltAngle={endTiltAngle}
      sx={{ position: "relative", overflow: "hidden" }}
    >
      {/* Title - Bigger & Left Aligned */}
      <MKTypography
        variant="h1"
        color="white"
        fontWeight="bold"
        sx={{
          fontSize: "80px",
          textShadow: "3px 3px black",
          textAlign: "left",
          whiteSpace: "pre-line",
          // lineHeight: 1.2,
          letterSpacing: 1.5,
        }}
      >
        {title.split(" ").map((word, index) => (
          <div key={index}>{word}</div>
        ))}
      </MKTypography>

      {/* Description */}
      <MKTypography
        variant="body1"
        color="white"
        mt={2}
        sx={{
          textAlign: "left",
        }}
      >
        {description}
      </MKTypography>

      {/* Custom Children */}
      {children && <Box mt={2}>{children}</Box>}

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="left" mt={3}>
        {buttons?.map((button, index) => (
          <Grid item key={index} display="flex" alignItems="center">
            {circularButtons ? (
              <>
                <IconButton
                  component="a"
                  href={button.href}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    border: "3px solid black",
                    boxShadow: "4px 4px 0px rgba(0,0,0,0.4)",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.1)",
                      boxShadow: "6px 6px 0px rgba(0,0,0,0.4)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "3px 3px 0px rgba(0,0,0,0.4)",
                    },
                    marginRight: 1,
                  }}
                >
                  <Iconify icon={button.icon || "mdi:arrow-top-right"} width={24} height={24} />
                </IconButton>

                {/* Clickable Text Outside Button */}
                <MKTypography
                  variant="h5"
                  color={button.disabled ? "white" : "white"}
                  fontWeight="bold"
                  ml={1}
                  component={button.disabled ? "span" : "a"} // avoid clickable link if disabled
                  href={button.disabled ? undefined : button.href}
                  onClick={button.disabled ? undefined : button.onClick}
                  sx={{
                    textDecoration: "none",
                    cursor: button.disabled ? "not-allowed" : "pointer",
                    "&:hover": button.disabled
                      ? {}
                      : {
                          textDecoration: "underline",
                        },
                    pointerEvents: button.disabled ? "none" : "auto", // fully disables interaction
                  }}
                >
                  {button.label}
                </MKTypography>
              </>
            ) : (
              <MKButton
                variant="contained"
                color={button.color}
                href={button.href}
                disabled={button.disabled}
                startIcon={<Iconify icon={button.icon} width={24} height={24} />}
              >
                {button.label}
              </MKButton>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Image - Positioned at Bottom Right */}
      {image && (
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            right: 10,
            width: "35%",
            maxWidth: "140px",
            zIndex: 1,
          }}
        >
          <img
            src={image}
            alt="Mascot"
            style={{
              width: "100%",
              height: "auto",
            }}
          />
        </Box>
      )}
    </CartoonCard>
  );
}

// Default Props
CartoonCardBasicSide.defaultProps = {
  tilt: false,
  baseTileAngle: -2,
  endTiltAngle: 3,
  color: "#ff4e50",
  circularButtons: false, // Default to false
};

// Prop Type Checking
CartoonCardBasicSide.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      href: PropTypes.string,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      color: PropTypes.string,
    })
  ),
  color: PropTypes.string,
  tilt: PropTypes.bool,
  baseTileAngle: PropTypes.number,
  endTiltAngle: PropTypes.number,
  circularButtons: PropTypes.bool, // Enables circular button style
  children: PropTypes.node,
  image: PropTypes.string,
};
